import type { FC } from 'react';
import { useMemo } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { VictoryBar, VictoryChart, VictoryAxis, VictoryTooltip, VictoryVoronoiContainer } from 'victory';

export interface DemographicsStats {
  reach_18_24_stats: number;
  reach_25_34_stats: number;
  reach_35_44_stats: number;
  reach_45_54_stats: number;
  reach_55_64_stats: number;
  reach_65_plus_stats: number;
}

interface IProps {
  stats: DemographicsStats;
}

const formatAxisTick = (t: number) => {
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    compactDisplay: 'short',
  }).format(t);
};

const AgeReachChart: FC<IProps> = ({ stats }) => {
  const theme = useTheme();

  const chartData = useMemo(() => {
    return [
      { x: '18-24', y: stats.reach_18_24_stats || 0 },
      { x: '25-34', y: stats.reach_25_34_stats || 0 },
      { x: '35-44', y: stats.reach_35_44_stats || 0 },
      { x: '45-54', y: stats.reach_45_54_stats || 0 },
      { x: '55-64', y: stats.reach_55_64_stats || 0 },
      { x: '65+', y: stats.reach_65_plus_stats || 0 },
    ];
  }, [stats]);

  return (
    <Box
      sx={{
        width: '100%',
        height: 350,
        bgcolor: 'background.paper',
        borderRadius: 2,
        p: 2,
        boxShadow: 1,
      }}
    >
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'text.secondary' }}>
        Age Reach
      </Typography>
      <VictoryChart domainPadding={{ x: 30 }} height={300} width={500} containerComponent={<VictoryVoronoiContainer />}>
        <VictoryAxis
          style={{
            axis: { stroke: theme.palette.divider },
            tickLabels: {
              fontSize: 12,
              fill: theme.palette.text.secondary,
              padding: 5,
            },
            grid: { stroke: 'none' },
          }}
        />

        <VictoryAxis
          dependentAxis
          tickFormat={formatAxisTick}
          style={{
            axis: { stroke: 'none' },
            tickLabels: {
              fontSize: 10,
              fill: theme.palette.text.disabled,
              padding: 5,
            },
            grid: {
              stroke: theme.palette.divider,
              strokeDasharray: '4, 5',
              strokeOpacity: 0.5,
            },
          }}
        />

        <VictoryBar
          data={chartData}
          cornerRadius={{ top: 6 }}
          animate={{
            duration: 500,
            onLoad: { duration: 500 },
          }}
          labelComponent={
            <VictoryTooltip
              flyoutStyle={{
                stroke: theme.palette.divider,
                strokeWidth: 1,
                fill: theme.palette.background.paper,
              }}
              style={{
                fill: theme.palette.text.primary,
                fontSize: 12,
              }}
              cornerRadius={4}
              pointerLength={8}
            />
          }
          labels={({ datum }: { datum: { y: number } }) => datum.y.toLocaleString()}
          style={{
            data: {
              fill: '#00acc1',
              width: 36,
              fillOpacity: 0.9,
            },
            labels: { fontSize: 12 },
          }}
        />
      </VictoryChart>
    </Box>
  );
};

export default AgeReachChart;
